import type { ProgramSlideLayoutV1 } from '../types/programContentSlideCanvas';
import { forEachBitmapImage } from './slideBitmapImageBlocks';
import {
  deleteSlideImageByPublicUrl,
  isSlideImageStoragePublicUrl,
  uploadSlideImage,
} from '../services/slideImageStorageService';

type PendingEntry = {
  blob: Blob;
  mimeType: string;
  objectUrl: string;
  blockId: string;
};

const pendingByObjectUrl = new Map<string, PendingEntry>();
const pendingByBlockId = new Map<string, PendingEntry>();

export function isPendingSlideImageSrc(src: string): boolean {
  return src.trim().startsWith('blob:');
}

/** 블럭 복사·붙여넣기: 기존 blob 미리보기를 새 블럭 id용으로 복제 */
export function clonePendingClipboardImageForBlock(src: string, newBlockId: string): string | null {
  const trimmed = src.trim();
  if (!trimmed.startsWith('blob:')) return null;
  const entry = pendingByObjectUrl.get(trimmed);
  if (!entry) return null;
  return registerPendingSlideImage(newBlockId, entry.blob, entry.mimeType);
}

export function registerPendingClipboardImage(blockId: string, blob: Blob, mimeType: string): string {
  return registerPendingSlideImage(blockId, blob, mimeType);
}

export function registerPendingSlideImage(blockId: string, blob: Blob, mimeType: string): string {
  revokePendingForBlock(blockId);
  const objectUrl = URL.createObjectURL(blob);
  const entry: PendingEntry = { blob, mimeType, objectUrl, blockId };
  pendingByObjectUrl.set(objectUrl, entry);
  pendingByBlockId.set(blockId, entry);
  return objectUrl;
}

export function revokePendingSlideImageSrc(src: string): void {
  const trimmed = src.trim();
  if (!trimmed.startsWith('blob:')) return;
  const entry = pendingByObjectUrl.get(trimmed);
  if (!entry) return;
  URL.revokeObjectURL(entry.objectUrl);
  pendingByObjectUrl.delete(trimmed);
  pendingByBlockId.delete(entry.blockId);
}

export function revokePendingForBlock(blockId: string): void {
  const entry = pendingByBlockId.get(blockId);
  if (entry) revokePendingSlideImageSrc(entry.objectUrl);
}

export function revokeAllPendingInLayout(layout: ProgramSlideLayoutV1): void {
  forEachBitmapImage(layout, (_kind, img) => {
    const src = img.src?.trim();
    if (src && isPendingSlideImageSrc(src)) revokePendingSlideImageSrc(src);
  });
}

export function revokeAllPendingInLayouts(layouts: Record<number, ProgramSlideLayoutV1>): void {
  for (const layout of Object.values(layouts)) {
    if (layout) revokeAllPendingInLayout(layout);
  }
}

async function finalizeBitmapImagesArray(
  images: ProgramSlideLayoutV1['clipboardImages']
): Promise<{ images: ProgramSlideLayoutV1['clipboardImages']; changed: boolean }> {
  if (!images.length) return { images, changed: false };

  let changed = false;
  const next = await Promise.all(
    images.map(async (img) => {
      const src = img.src?.trim() ?? '';
      if (!src || !isPendingSlideImageSrc(src)) return img;
      const entry = pendingByObjectUrl.get(src);
      if (!entry) {
        changed = true;
        return { ...img, src: '' };
      }
      const { publicUrl } = await uploadSlideImage(entry.blob, entry.mimeType);
      revokePendingSlideImageSrc(src);
      changed = true;
      return { ...img, src: publicUrl };
    })
  );

  return { images: next, changed };
}

/** 슬라이드 저장 직전: blob URL → Supabase Storage public URL (클립보드·스캔 이미지) */
export async function finalizePendingSlideImagesInLayout(
  layout: ProgramSlideLayoutV1
): Promise<ProgramSlideLayoutV1> {
  const [clipboardResult, scanResult, webpagesResult] = await Promise.all([
    finalizeBitmapImagesArray(layout.clipboardImages ?? []),
    finalizeBitmapImagesArray(layout.scanImages ?? []),
    finalizeWebpageThumbsArray(layout.webpages ?? []),
  ]);

  if (!clipboardResult.changed && !scanResult.changed && !webpagesResult.changed) return layout;

  return {
    ...layout,
    clipboardImages: clipboardResult.images,
    scanImages: scanResult.images,
    webpages: webpagesResult.webpages,
  };
}

async function finalizeWebpageThumbsArray(
  webpages: NonNullable<ProgramSlideLayoutV1['webpages']>
): Promise<{ webpages: NonNullable<ProgramSlideLayoutV1['webpages']>; changed: boolean }> {
  if (!webpages.length) return { webpages, changed: false };
  let changed = false;
  const next = await Promise.all(
    webpages.map(async (wp) => {
      const src = wp.thumbSrc?.trim() ?? '';
      if (!src || !isPendingSlideImageSrc(src)) return wp;
      const entry = pendingByObjectUrl.get(src);
      if (!entry) {
        changed = true;
        return { ...wp, thumbSrc: '' };
      }
      const { publicUrl } = await uploadSlideImage(entry.blob, entry.mimeType);
      revokePendingSlideImageSrc(src);
      changed = true;
      return { ...wp, thumbSrc: publicUrl };
    })
  );
  return { webpages: next, changed };
}

/** @deprecated finalizePendingSlideImagesInLayout 사용 */
export const finalizeClipboardImagesInLayout = finalizePendingSlideImagesInLayout;

/** 저장된 슬라이드 레이아웃에 등장하는 slide-images public URL 집합 */
export function collectSlideImageStorageUrlsFromLayouts(
  layouts: Record<number, ProgramSlideLayoutV1>
): Set<string> {
  const urls = new Set<string>();
  for (const layout of Object.values(layouts)) {
    if (!layout) continue;
    forEachBitmapImage(layout, (_kind, img) => {
      const src = img.src?.trim() ?? '';
      if (src && isSlideImageStoragePublicUrl(src)) urls.add(src);
    });
    for (const wp of layout.webpages ?? []) {
      const thumb = wp.thumbSrc?.trim() ?? '';
      if (thumb && isSlideImageStoragePublicUrl(thumb)) urls.add(thumb);
    }
  }
  return urls;
}

/** layout_json에서 빠진 URL만 Storage·용량 DB에서 해제 (고아 파일 방지) */
export async function releaseRemovedSlideImageUrls(
  previousUrls: Set<string>,
  nextUrls: Set<string>
): Promise<void> {
  const tasks: Array<Promise<void>> = [];
  previousUrls.forEach((url) => {
    if (!nextUrls.has(url)) {
      tasks.push(deleteSlideImageByPublicUrl(url).catch(() => undefined));
    }
  });
  await Promise.all(tasks);
}
